import { useState } from "react";
import { Link } from "react-router-dom";
import API, { formatError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Gem, ArrowLeft, Mail } from "lucide-react";
import { useSiteSettings } from "../contexts/SiteSettingsContext";

export default function ForgotPasswordPage() {
  const { siteTitle } = useSiteSettings();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await API.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center px-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-[#00F0FF]/10 flex items-center justify-center">
              <Gem className="w-6 h-6 text-[#00F0FF]" />
            </div>
            <span className="text-2xl font-bold text-[#F8FAFC]">{siteTitle}</span>
          </Link>
        </div>

        <div className="bg-[#131722] rounded-xl border border-[#2A3241] p-8" data-testid="forgot-form">
          {!sent ? (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="text-center mb-4">
                <Mail className="w-10 h-10 text-[#00F0FF] mx-auto mb-3" />
                <h1 className="text-xl font-bold text-[#F8FAFC]">Reset Password</h1>
                <p className="text-sm text-[#94A3B8] mt-1">Enter your email and we'll send a reset link</p>
              </div>
              {error && <div className="p-3 rounded-lg bg-[#EF4444]/10 border border-[#EF4444]/30 text-sm text-[#EF4444]">{error}</div>}
              <div>
                <Label className="text-[#94A3B8] text-sm mb-2 block">Email</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="your@email.com"
                  className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] focus:border-[#00F0FF]" data-testid="forgot-email-input" />
              </div>
              <button type="submit" disabled={loading} data-testid="forgot-submit-btn"
                className="w-full py-3 text-sm font-bold uppercase tracking-widest bg-[#00F0FF] text-[#0B0E14] rounded-lg hover:bg-[#00F0FF]/90 disabled:opacity-50">
                {loading ? "Sending..." : "Send Reset Link"}
              </button>
            </form>
          ) : (
            <div className="text-center py-4">
              <div className="w-16 h-16 rounded-full bg-[#10B981]/10 flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-[#10B981]" />
              </div>
              <h2 className="text-lg font-bold text-[#F8FAFC] mb-2">Check Your Email</h2>
              <p className="text-sm text-[#94A3B8]">If an account exists for {email}, we've sent a password reset link.</p>
            </div>
          )}
          <div className="mt-4 text-center">
            <Link to="/login" className="text-sm text-[#00F0FF] hover:underline flex items-center justify-center gap-1">
              <ArrowLeft className="w-3 h-3" /> Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
